import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend
} from 'recharts';
import { motion } from 'framer-motion';
import {
  BarChart2, TrendingUp, Wallet, Smartphone, Printer, Globe, Wifi, ShoppingCart,
  ArrowRight, Loader2, ExternalLink, DollarSign, Briefcase
} from 'lucide-react';
import { FaDownload } from 'react-icons/fa';
import axiosInstance from '../../utils/axiosConfig';
import { Link } from 'react-router-dom';
import CustomSelect from './CustomSelect';
import GastosPorCentroCosto from './GastosPorCentroCosto';
import '../AssetsView.css';
import { SURA_BLUE, shadeColor, mesesVisibles } from './palette';

// Todo el módulo en tonos de azul, de navy oscuro a celeste claro — cada
// tipo tiene un matiz/tono propio para diferenciarse sin salirse del azul.
const TYPE_CFG = {
  TELEFONIA:  { label:'Telefonía',   icon: Smartphone,   color: '#003366', bg: '#EFF6FF', border: '#BFDBFE' }, // azul navy
  IMPRESORAS: { label:'Impresoras',  icon: Printer,      color: '#34568B', bg: '#EEF2F8', border: '#C7D3E8' }, // azul acero
  CONSULTORIA:{ label:'Consultoría', icon: Briefcase,    color: '#0B5D8A', bg: '#EAF6FB', border: '#BFE3F0' }, // azul petróleo
  INTERNET:   { label:'Internet',    icon: Globe,        color: '#0062A5', bg: '#EAF4FC', border: '#BEDCF5' }, // azul medio
  STARLINK:   { label:'Starlink',    icon: Wifi,         color: '#0891B2', bg: '#ECFEFF', border: '#A5F3FC' }, // azul teal/cian
  COMPRAS:    { label:'Compras',     icon: ShoppingCart, color: '#29ABE2', bg: '#E8F6FE', border: '#B8E4F7' }, // azul azure
  GENERAL:    { label:'Generales',   icon: Globe,        color: '#7EC8E3', bg: '#F0FAFD', border: '#C7EAF5' }, // azul celeste claro
};

// Variantes de sombra para diferenciar conceptos que comparten el mismo tipo
// (ej. Movistar/Digitel son ambos TELEFONIA) sin salirse del color de su tipo.
const SHADE_STEPS = [0, 32, -24, 45, -40];

// Color de cada concepto en la gráfica: el color de su tipo (mismo que su
// tarjeta en "Acceso Rápido"), con una variante de tono si comparte tipo
// con otros conceptos, para que ambas vistas queden relacionadas.
const getConceptColor = (concepto, conceptos) => {
  const base = (TYPE_CFG[concepto.tipo] || TYPE_CFG.GENERAL).color;
  const mismoTipo = conceptos.filter(c => c.tipo === concepto.tipo);
  const idx = mismoTipo.findIndex(c => c.id === concepto.id);
  if (idx <= 0) return base;
  return shadeColor(base, SHADE_STEPS[idx % SHADE_STEPS.length]);
};

/* ─── Helpers ──────────────────────────────────────────────────── */
const fmtK  = (v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${Number(v||0).toFixed(0)}`;
const fmtFull = (v) => `$${Number(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

const MESES     = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const MESES_SHORT = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];

/* ─── KPI Card ─────────────────────────────────────────────────── */
const KpiCard = ({ title, value, subtitle, icon: Icon, color, bg }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
    {/* accent line top */}
    <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: color }} />
    <div className="flex justify-between items-start mb-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</p>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
        <Icon size={17} style={{ color }} />
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-900 font-mono">{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

/* ─── Custom Tooltip Bar ───────────────────────────────────────── */
const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 min-w-[160px]">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-3 py-0.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.fill }} />
            <span className="text-xs text-gray-600">{p.name}</span>
          </div>
          <span className="text-xs font-bold text-gray-900">{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── Concepto Card ────────────────────────────────────────────── */
const ConceptoCard = ({ concepto, total, route, delay, mesFiltro }) => {
  const cfg = TYPE_CFG[concepto.tipo] || TYPE_CFG.GENERAL;
  const IconComp = cfg.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: 'easeOut' }}
    >
      <Link to={route} className="block no-underline h-full">
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm h-full flex flex-col justify-between group cursor-pointer"
          style={{ transition: 'box-shadow 0.2s, border-color 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow='0 8px 24px rgba(0,51,102,0.12)'; e.currentTarget.style.borderColor=cfg.color+'55'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow=''; e.currentTarget.style.borderColor=''; }}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cfg.bg }}>
                <IconComp size={19} style={{ color: cfg.color }} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900 m-0 leading-tight">{concepto.nombre}</h4>
                <p className="text-xs text-gray-400 m-0 mt-0.5">{concepto.proveedor}</p>
              </div>
            </div>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
              {cfg.label}
            </span>
          </div>

          {/* Total */}
          <div className="mb-4">
            <p className="text-xs text-gray-400 mb-0.5">
              {mesFiltro ? `Total ${mesFiltro.charAt(0) + mesFiltro.slice(1).toLowerCase()}` : 'Total acumulado'}
            </p>
            <p className="text-xl font-bold font-mono" style={{ color: cfg.color }}>{fmtFull(total)}</p>
          </div>

          {/* CTA */}
          <div
            className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs font-semibold"
            style={{ color: cfg.color }}
          >
            <span>Ver detalles</span>
            <ArrowRight size={14} className="transform group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ─── Main Dashboard ───────────────────────────────────────────── */
const PresupuestosDashboardView = () => {
  const [conceptos, setConceptos]           = useState([]);
  const [porConceptoRaw, setPorConceptoRaw] = useState([]);
  const [ccData, setCcData]                 = useState([]);
  const [loading, setLoading]               = useState(true);
  const [anioActual, setAnioActual]         = useState(new Date().getFullYear());
  const [mesFiltroCC, setMesFiltroCC]       = useState(''); // '' = todo el año
  const [exportando, setExportando]         = useState(false);

  useEffect(() => { fetchData(); }, [anioActual, mesFiltroCC]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const mesParam = mesFiltroCC ? `&mes=${mesFiltroCC}` : '';
      const [cRes, dRes] = await Promise.all([
        axiosInstance.get('/api/presupuestos/conceptos'),
        axiosInstance.get(`/api/presupuestos/dashboard?anio=${anioActual}${mesParam}`)
      ]);
      setConceptos(cRes.data);
      setPorConceptoRaw(dRes.data.por_concepto || []);
      // Solo se descarta basura real de data vieja; los fallbacks legítimos de
      // "sede sin distribución configurada" (ver Internet) deben verse siempre,
      // para que el dinero no desaparezca en silencio del desglose.
      const validPrefixes = ['001','002','003','004','005','006','007','008','009','010','011','012','013','014','015','016','017','018','019'];
      const sedesFallback = ['CA1', 'CDN', 'Presidencia', 'Almacén', 'Planta', 'Sin asignar'];
      const rawCC = dRes.data.por_centro_costo || [];
      const cleanCC = rawCC.filter(c =>
        validPrefixes.some(p => (c.centro_costo||'').startsWith(p)) ||
        sedesFallback.includes(c.centro_costo)
      );
      setCcData(cleanCC.slice(0, 12)); // top 12
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  /* Exporta el detalle de gastos por centro de costo del período seleccionado
     en "Centro de Costo" (mes puntual, o todo lo cargado del año si no hay mes) */
  const handleExportarExcel = async () => {
    setExportando(true);
    try {
      const mesParam = mesFiltroCC ? `&mes=${mesFiltroCC}` : '';
      const res = await axiosInstance.get(
        `/api/presupuestos/exportar-excel?anio=${anioActual}${mesParam}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const sufijoMes = mesFiltroCC ? mesFiltroCC.charAt(0) + mesFiltroCC.slice(1).toLowerCase() : 'AnioCompleto';
      link.setAttribute('download', `Gastos_Detallados_${sufijoMes}_${anioActual}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando Excel:', err);
    } finally {
      setExportando(false);
    }
  };

  /* Monthly bar data — stacked per concepto */
  const barData = useMemo(() => {
    const visibles = mesesVisibles(anioActual); // no mostrar meses futuros del año en curso
    const map = {};
    MESES.forEach((m, i) => { if (visibles.includes(m)) map[m] = { name: MESES_SHORT[i] }; });
    porConceptoRaw.forEach(item => {
      if (!map[item.mes]) return;
      map[item.mes][item.concepto_nombre] = (map[item.mes][item.concepto_nombre] || 0) + item.total;
    });
    return Object.values(map);
  }, [porConceptoRaw, anioActual]);

  /* Totals */
  const totalesPorTipo = useMemo(() => {
    const acc = { TELEFONIA: 0, IMPRESORAS: 0, GENERAL: 0, INTERNET: 0, STARLINK: 0, COMPRAS: 0, CONSULTORIA: 0 };
    porConceptoRaw.forEach(item => {
      const c = conceptos.find(x => x.id === item.concepto_id);
      if (c) acc[c.tipo] = (acc[c.tipo] || 0) + item.total;
    });
    return acc;
  }, [porConceptoRaw, conceptos]);

  const totalAnio = totalesPorTipo.TELEFONIA + totalesPorTipo.IMPRESORAS + totalesPorTipo.GENERAL + totalesPorTipo.INTERNET + totalesPorTipo.STARLINK + totalesPorTipo.COMPRAS + totalesPorTipo.CONSULTORIA;

  /* Per-concepto totals — respeta el filtro de mes de "Centro de Costo"
     (si hay un mes seleccionado, solo suma ese mes; si no, el año completo) */
  const conceptoTotals = useMemo(() => {
    const acc = {};
    porConceptoRaw.forEach(item => {
      if (mesFiltroCC && item.mes !== mesFiltroCC) return;
      acc[item.concepto_nombre] = (acc[item.concepto_nombre] || 0) + item.total;
    });
    return acc;
  }, [porConceptoRaw, mesFiltroCC]);

  /* Nombre a mostrar por tipo: si el tipo tiene un único concepto activo,
     usar su nombre real (ej. "Proyectos") en vez de la etiqueta genérica
     del tipo, para que nunca quede desactualizado si el concepto cambia. */
  const nombrePorTipo = (tipo) => {
    const delTipo = conceptos.filter(c => c.tipo === tipo);
    if (delTipo.length === 1) return delTipo[0].nombre;
    return TYPE_CFG[tipo].label;
  };

  /* Pie for tipo — mismos colores que las tarjetas/badges de tipo (TYPE_CFG) */
  const pieData = [
    { name: nombrePorTipo('TELEFONIA'),   value: totalesPorTipo.TELEFONIA,   color: TYPE_CFG.TELEFONIA.color },
    { name: nombrePorTipo('IMPRESORAS'),  value: totalesPorTipo.IMPRESORAS,  color: TYPE_CFG.IMPRESORAS.color },
    { name: nombrePorTipo('GENERAL'),     value: totalesPorTipo.GENERAL,     color: TYPE_CFG.GENERAL.color },
    { name: nombrePorTipo('INTERNET'),    value: totalesPorTipo.INTERNET,    color: TYPE_CFG.INTERNET.color },
    { name: nombrePorTipo('STARLINK'),    value: totalesPorTipo.STARLINK,    color: TYPE_CFG.STARLINK.color },
    { name: nombrePorTipo('COMPRAS'),     value: totalesPorTipo.COMPRAS,     color: TYPE_CFG.COMPRAS.color },
    { name: nombrePorTipo('CONSULTORIA'), value: totalesPorTipo.CONSULTORIA, color: TYPE_CFG.CONSULTORIA.color },
  ].filter(d => d.value > 0);

  const getRoute = (c) => {
    if (c.tipo === 'TELEFONIA')  return `/presupuestos/telefonia/${c.id}`;
    if (c.tipo === 'IMPRESORAS') return `/presupuestos/impresoras/${c.id}`;
    if (c.tipo === 'INTERNET') return `/presupuestos/internet/${c.id}`;
    if (c.tipo === 'STARLINK') return `/presupuestos/starlink/${c.id}`;
    if (c.tipo === 'COMPRAS') return `/presupuestos/compras/${c.id}`;
    if (c.tipo === 'CONSULTORIA') return `/presupuestos/consultoria/${c.id}`;
    return `/presupuestos/generales/${c.id}`;
  };

  return (
    <div className="assets-view">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="page-title flex items-center gap-2 m-0">
            <BarChart2 size={22} style={{ color: SURA_BLUE }} />
            Control de Gastos de TI
          </h2>
          <p className="text-gray-400 text-sm mt-1">Dashboard de consumos y servicios corporativos — {anioActual}</p>
        </div>
        <CustomSelect
          value={anioActual}
          onChange={e => setAnioActual(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium outline-none"
          style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
        >
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </CustomSelect>
      </div>

      {loading ? (
        <div className="flex justify-center py-28">
          <Loader2 size={36} className="animate-spin" style={{ color: SURA_BLUE }} />
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">

          {/* ── KPI Row ────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: 'Gasto Total', value: fmtFull(totalAnio), icon: Wallet, color: SURA_BLUE, bg: '#EFF6FF', subtitle: `Año ${anioActual}` },
              { title: 'Telefonía', value: fmtFull(totalesPorTipo.TELEFONIA), icon: Smartphone, color: TYPE_CFG.TELEFONIA.color, bg: TYPE_CFG.TELEFONIA.bg, subtitle: `${totalAnio > 0 ? Math.round(totalesPorTipo.TELEFONIA/totalAnio*100) : 0}% del total` },
              { title: 'Impresión', value: fmtFull(totalesPorTipo.IMPRESORAS), icon: Printer, color: TYPE_CFG.IMPRESORAS.color, bg: TYPE_CFG.IMPRESORAS.bg, subtitle: `${totalAnio > 0 ? Math.round(totalesPorTipo.IMPRESORAS/totalAnio*100) : 0}% del total` },
              { title: 'Generales', value: fmtFull(totalesPorTipo.GENERAL), icon: Globe, color: TYPE_CFG.GENERAL.color, bg: TYPE_CFG.GENERAL.bg, subtitle: `${totalAnio > 0 ? Math.round(totalesPorTipo.GENERAL/totalAnio*100) : 0}% del total` },
            ].map((kpi, i) => (
              <motion.div key={kpi.title} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay: i*0.07 }}>
                <KpiCard {...kpi} />
              </motion.div>
            ))}
          </div>

          {/* ── Main Charts Row ────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Evolución mensual — span 2 cols */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} style={{ color: SURA_BLUE }} />
                  <h3 className="text-xs font-bold uppercase tracking-wider m-0" style={{ color: SURA_BLUE }}>
                    Evolución Mensual de Gastos
                  </h3>
                </div>
                <span className="text-xs text-gray-400 font-medium">{anioActual}</span>
              </div>
              <div className="flex-1 min-h-[380px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} barCategoryGap="30%" margin={{ top: 4, right: 4, left: -4, bottom: 0 }} style={{ outline: 'none' }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtK} />
                    <Tooltip content={<CustomBarTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Legend
                      formatter={v => <span style={{ fontSize: 12, color: '#64748b', marginLeft: 3 }}>{v}</span>}
                      iconType="circle"
                      iconSize={9}
                      wrapperStyle={{ paddingTop: 18 }}
                    />
                    {conceptos.map((c, i) => (
                      <Bar
                        key={c.id}
                        dataKey={c.nombre}
                        stackId="stack"
                        fill={getConceptColor(c, conceptos)}
                        radius={i === conceptos.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Distribución por tipo — donut */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-6">
                <DollarSign size={16} style={{ color: SURA_BLUE }} />
                <h3 className="text-xs font-bold uppercase tracking-wider m-0" style={{ color: SURA_BLUE }}>
                  Distribución por Tipo
                </h3>
              </div>

              {pieData.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-sm text-gray-400 italic">Sin datos</div>
              ) : (
                <>
                  <div className="flex-1">
                    <ResponsiveContainer width="100%" height={190}>
                      <PieChart style={{ outline: 'none' }}>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%" cy="50%"
                          outerRadius={80} innerRadius={50}
                          paddingAngle={4}
                          strokeWidth={0}
                        >
                          {pieData.map(d => <Cell key={d.name} fill={d.color} />)}
                        </Pie>
                        <Tooltip formatter={v => fmtFull(v)} contentStyle={{ borderRadius: 10, border: '1px solid #f1f5f9', fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Progress bars */}
                  <div className="space-y-3 mt-2">
                    {pieData.map(d => {
                      const pct = totalAnio > 0 ? Math.round(d.value / totalAnio * 100) : 0;
                      return (
                        <div key={d.name}>
                          <div className="flex justify-between items-center mb-1">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                              <span className="text-xs font-medium text-gray-600">{d.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-gray-500">{fmtFull(d.value)}</span>
                              <span className="text-xs font-bold w-8 text-right" style={{ color: d.color }}>{pct}%</span>
                            </div>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full rounded-full"
                              style={{ background: d.color }}
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Centro de Costo ────────────────────────────────── */}
          <GastosPorCentroCosto
            data={ccData}
            emptyText={mesFiltroCC ? `Sin gastos registrados en ${mesFiltroCC.charAt(0) + mesFiltroCC.slice(1).toLowerCase()} ${anioActual}` : 'Sin datos para este período'}
            headerRight={
              <div className="flex items-center gap-1.5">
                <CustomSelect
                  value={mesFiltroCC}
                  onChange={e => setMesFiltroCC(e.target.value)}
                  className="px-2.5 py-1 rounded-full text-xs font-semibold text-gray-500 bg-gray-50 hover:bg-gray-100 hover:text-gray-700 outline-none cursor-pointer transition-colors"
                >
                  <option value="">Todo el año</option>
                  {MESES.map(m => <option key={m} value={m}>{m.charAt(0) + m.slice(1).toLowerCase()}</option>)}
                </CustomSelect>
                <span className="text-xs text-gray-400 font-medium">{anioActual}</span>
              </div>
            }
          />

          {/* ── Acceso Rápido ──────────────────────────────────── */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <ExternalLink size={15} className="text-gray-400" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 m-0">
                Acceso Rápido por Concepto
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {conceptos.map((concepto, i) => (
                <ConceptoCard
                  key={concepto.id}
                  concepto={concepto}
                  total={conceptoTotals[concepto.nombre] || 0}
                  route={getRoute(concepto)}
                  delay={i * 0.06}
                  mesFiltro={mesFiltroCC}
                />
              ))}
            </div>
          </div>

          {/* ── Exportar a Excel ───────────────────────────────── */}
          <div className="flex justify-end">
            <button
              className="btn-secondary"
              onClick={handleExportarExcel}
              disabled={exportando}
              title={
                mesFiltroCC
                  ? `Exporta el detalle de gastos por centro de costo de ${mesFiltroCC.charAt(0) + mesFiltroCC.slice(1).toLowerCase()} ${anioActual}`
                  : `Exporta el detalle de gastos por centro de costo acumulado del año ${anioActual}`
              }
            >
              {exportando ? <Loader2 size={14} className="animate-spin" /> : <FaDownload />}
              Exportar a Excel
            </button>
          </div>

        </motion.div>
      )}
    </div>
  );
};

export default PresupuestosDashboardView;
